import { exerciseBodyParts, MainExercise } from "@domain/exercise/exercise";
import { DayIndex, dayIndexes, ProgramDays } from "@domain/program/program-days";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { offsetArray } from "@utils/array-utils";
import { isNil, isNotNil } from "@utils/assert";
import { WEEK_LENGTH } from "@utils/date-utils";
import { positiveModulo } from "@utils/number-utils";
import { capitalizeFirstLetter } from "@utils/string-utils";
import dayjs, { Dayjs } from "dayjs";

export interface ProgramContent {
	days: ProgramDays;
	name: string;
	startDate?: Dayjs;
	endDate?: Dayjs;
}

export interface Program extends ProgramContent {
	id: string;
	patientId: string;
	status: ProgramStatus;
}

export interface ProgramUpdateData extends Omit<ProgramContent, "startDate"> {
	startDate?: Dayjs;
}

export interface NewProgram extends Omit<Program, "id" | "name"> {
	status: ProgramStatus.Draft;
	name?: string;
}

export interface ProgramMetadata {
	id: string;
	status: ProgramStatus;
	startDate?: Dayjs;
	endDate?: Dayjs;
}

export const getEmptyNewProgram = (patientId: string): NewProgram => ({
	patientId,
	days: new ProgramDays(),
	status: ProgramStatus.Draft,
});

export function programContentAreEquals(programContent1: ProgramContent, programContent2: ProgramContent): boolean {
	const nameEquals =
		isNotNil(programContent1.name || programContent2.name) && programContent1.name === programContent2.name;
	const startDateEquals =
		!(programContent1.startDate && programContent2.startDate) ||
		!!programContent1.startDate.isSame(programContent2.startDate, "date");
	const endDateEquals =
		!(programContent1.endDate && programContent2.endDate) ||
		!!programContent1.endDate.isSame(programContent2.endDate, "date");

	return nameEquals && startDateEquals && endDateEquals && programContent1.days.equals(programContent2.days);
}

export function isNewProgram(program: Program | NewProgram): program is NewProgram {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	return (program as Program).id === undefined;
}

// TODO WTF? (map + reduce & "&&")
export function getProgramDetails(days: ProgramDays): ProgramDetails {
	const emptyProgramDetails: ProgramDetails = {
		exerciseTitles: [],
		exerciseUnits: [],
		exerciseSets: [],
		exerciseIds: [],
		exerciseRevisions: [],
		exerciseCategories: [],
		exerciseBodyParts: [],
		exerciseDifficulties: [],
		exerciseStartingPosition: [],
	};
	return days
		.map(([, session]) => session)
		.reduce((accDay, day) => {
			const exerciseDetails = day.exercises.reduce(
				(accExercise, exercise) => ({
					exerciseTitles: [...accExercise.exerciseTitles, exercise.title],
					exerciseUnits: [...accExercise.exerciseUnits, exercise.units],
					exerciseSets: [...accExercise.exerciseSets, exercise.setCount],
					exerciseIds: [...accExercise.exerciseIds, exercise.id],
					exerciseRevisions: [...accExercise.exerciseRevisions, exercise.revision],
					exerciseCategories: [...accExercise.exerciseCategories, exercise.category],
					exerciseBodyParts: [...accExercise.exerciseBodyParts, exerciseBodyParts(exercise).join("&&")],
					exerciseDifficulties: [...accExercise.exerciseDifficulties, exercise.difficulty],
					exerciseStartingPosition: [...accExercise.exerciseStartingPosition, exercise.startingPosition],
				}),
				emptyProgramDetails,
			);
			return {
				exerciseTitles: [...accDay.exerciseTitles, ...exerciseDetails.exerciseTitles],
				exerciseUnits: [...accDay.exerciseUnits, ...exerciseDetails.exerciseUnits],
				exerciseSets: [...accDay.exerciseSets, ...exerciseDetails.exerciseSets],
				exerciseIds: [...accDay.exerciseIds, ...exerciseDetails.exerciseIds],
				exerciseRevisions: [...accDay.exerciseRevisions, ...exerciseDetails.exerciseRevisions],
				exerciseCategories: [...accDay.exerciseCategories, ...exerciseDetails.exerciseCategories],
				exerciseBodyParts: [...accDay.exerciseBodyParts, ...exerciseDetails.exerciseBodyParts],
				exerciseDifficulties: [...accDay.exerciseDifficulties, ...exerciseDetails.exerciseDifficulties],
				exerciseStartingPosition: [...accDay.exerciseStartingPosition, ...exerciseDetails.exerciseStartingPosition],
			};
		}, emptyProgramDetails);
}

export interface ProgramDetails {
	exerciseTitles: string[];
	exerciseUnits: number[];
	exerciseSets: number[];
	exerciseIds: string[];
	exerciseRevisions: number[];
	exerciseCategories: string[];
	exerciseBodyParts: string[];
	exerciseDifficulties: string[];
	exerciseStartingPosition: string[];
}

export function isProgram(program: Program | NewProgram): program is Program {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	return (program as Program).id !== undefined;
}

export const formatDayColumn = (columnIndex: DayIndex, formattingType: "short" | "long" | "min"): string => {
	const isShort = formattingType === "short";
	const dayIndex = columnIndex % WEEK_LENGTH;
	let dayLabel: string;

	if (formattingType === "long") {
		dayLabel = dayjs.weekdays()[dayIndex];
	} else if (isShort) {
		dayLabel = dayjs.weekdaysShort()[dayIndex];
	} else {
		dayLabel = dayjs.weekdaysMin()[dayIndex];
	}

	return capitalizeFirstLetter(
		dayLabel,
	);
};

export const getDayColumnIndexes = (): DayIndex[] => offsetArray(dayIndexes, dayjs.localeData().firstDayOfWeek() - 1);

export const getLocaleDayColumnIndex = (index: number): number =>
	positiveModulo(index - dayjs.localeData().firstDayOfWeek(), 7);

export function programContentDeepCopy({ days, ...others }: ProgramContent): ProgramContent {
	return {
		...others,
		days: days.copy(),
	};
}

export const updateExerciseFromProgram = (
	program: ProgramContent,
	exerciseToUpdateDraggableId: string | undefined,
	newExercise: MainExercise | null,
): ProgramContent => {
	if (isNil(exerciseToUpdateDraggableId) || isNil(newExercise)) {
		return program;
	}

	return {
		...program,
		days: new ProgramDays(
			program.days.map(([dayIndex, session]) => [
				dayIndex,
				{
					exercises: session.exercises.map(e => (e.draggableId === exerciseToUpdateDraggableId ? newExercise : e)),
				},
			]),
		),
	};
};
